/**
 * AdHocQueryPage — `/reporting/adhoc`.
 *
 * Wired to real backend (B-slice 5):
 *   POST /v1/reporting/ad-hoc/queries — execute an ad-hoc query
 *   GET  /v1/reporting/ad-hoc/queries — list recent runs
 *
 * The query is bounded to registered report keys for safety.
 */
import { type JSX, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  runAdHocQuery,
  listAdHocQueryRuns,
  type AdHocQueryRunWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

const REPORT_KEYS = [
  'DAILY_REVENUE_BY_REGION',
  'MAIL_VOLUME_TRENDS',
  'CUSTOMER_ACQUISITION_FUNNEL',
  'DRIVER_PERFORMANCE',
  'WALLET_ACTIVITY_SUMMARY',
  'AUDIT_COMPLIANCE_HEATMAP',
  'NIPOST_PAY_ADOPTION',
  'GOVERNMENT_SERVICE_THROUGHPUT',
  'RECEIPT_INVOICE_KPIS',
] as const;

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export function AdHocQueryPage(): JSX.Element {
  const qc = useQueryClient();
  const [sourceKey, setSourceKey] = useState<string>(REPORT_KEYS[0]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const history = useQuery<readonly AdHocQueryRunWire[], Error>({
    queryKey: ['reporting', 'adhoc', 'queries'],
    queryFn: () => listAdHocQueryRuns(),
    retry: false,
  });

  const run = useMutation<AdHocQueryRunWire, Error>({
    mutationFn: () =>
      runAdHocQuery(sourceKey, {
        fromDate,
        toDate,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reporting', 'adhoc', 'queries'] });
    },
  });

  const denied =
    (run.isError && run.error instanceof ApiError &&
      (run.error.status === 401 || run.error.status === 403)) ||
    (history.isError && history.error instanceof ApiError &&
      (history.error.status === 401 || history.error.status === 403));

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    if (fromDate.length === 0 || toDate.length === 0) return;
    run.mutate();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ad-hoc query"
        description="Execute a bounded query against a registered data source — results are persisted."
      />

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/runAdHoc</code> required.
        </DeferredNotice>
      )}

      <form onSubmit={submit} className="bg-white border border-border rounded-xl p-5 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-3 flex flex-col gap-1">
            <label className="text-xs font-medium text-muted">Data source</label>
            <select
              value={sourceKey}
              onChange={(e) => setSourceKey(e.target.value)}
              aria-label="Source key"
              data-testid="adhoc-source-select"
              className="px-3 py-2 text-sm border border-border rounded-lg"
            >
              {REPORT_KEYS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted">From date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              aria-label="From date"
              data-testid="adhoc-from-date"
              className="px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted">To date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              aria-label="To date"
              data-testid="adhoc-to-date"
              className="px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={run.isPending || !fromDate || !toDate}
              data-testid="adhoc-run-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
            >
              <Search className="w-4 h-4" />
              {run.isPending ? 'Running…' : 'Run query'}
            </button>
          </div>
        </div>
      </form>

      {run.isError && !denied && (
        <div className="bg-white border border-red-200 rounded-xl p-5">
          <p className="text-sm text-red-600">{run.error.message}</p>
        </div>
      )}

      {run.data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">
            Latest result — <span className="font-mono text-xs text-muted">{run.data.sourceKey}</span>{' '}
            <span className={`text-xs px-2 py-0.5 rounded-full ${run.data.status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {run.data.status}
            </span>
          </h3>
          {run.data.failureReason && (
            <p className="text-sm text-red-600 mb-3">{run.data.failureReason}</p>
          )}
          <pre className="p-3 bg-gray-50 border border-border rounded-lg text-[11px] font-mono overflow-x-auto max-h-96">
            {JSON.stringify(run.data.result, null, 2)}
          </pre>
          <p className="text-[10px] text-muted mt-2">
            Duration: {run.data.durationMs ?? '—'} ms · Completed: {run.data.completedAt ? formatDateTime(run.data.completedAt) : '—'}
          </p>
        </div>
      )}

      <div className="bg-white border border-border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">
          Recent ad-hoc runs ({history.data?.length ?? 0})
        </h3>
        {history.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : history.data && history.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Started</th>
                  <th className="text-right px-3 py-2">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {history.data.map((r) => (
                  <tr key={r.id} data-testid="adhoc-history-row">
                    <td className="px-3 py-2 font-mono text-xs">{r.sourceKey}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${r.status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{formatDateTime(r.startedAt)}</td>
                    <td className="px-3 py-2 text-right text-xs">{r.durationMs ?? '—'} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted">No ad-hoc runs yet.</p>
        )}
      </div>
    </div>
  );
}
